import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Scale } from "lucide-react";
import { fmtMoedaBR, fmtKgBR } from "@/lib/formato-mercado";
import { fmtDataBR } from "@/lib/formato-mercado";
import type { ConferenciaDTO, ItemDoRomaneioParaConferencia } from "@shared/mercado-conferencia";

/**
 * "Bate com o combinado?" — o formulário de conferência, um só para os dois lugares.
 *
 * Usado pelo link público (`/v/:token`, sem conta) e pelo app do comprador (em
 * modal). O mesmo markup nos dois porque é o MESMO ato: duas versões divergiriam
 * na primeira regra nova, e a do link — que é a que o comprador de verdade usa —
 * seria a esquecida.
 *
 * ⚠️ KG E PREÇO SE CONFEREM (Hugo, 24/09): o produtor pode emitir um R$/kg que
 * não é o combinado. O que o comprador registra é a base do que ele deve; o
 * produtor vê a divergência. O VENCIMENTO também, mas só no app
 * (`comVencimento`): no link público quem escreve é qualquer um com o token, e o
 * prazo mora na venda do produtor — lá o servidor nem aceita o campo.
 *
 * ⚠️ Subtotal e total recalculam só para EXIBIÇÃO, enquanto se digita. O número
 * que vale é o que o servidor devolve depois de gravar — e é ele que a tela passa
 * a mostrar.
 *
 * Dois botões de largura cheia em vez de um formulário sempre aberto: o caso
 * comum é "bateu", e ele tem de custar um toque no celular, no pátio, com a
 * carga descendo do caminhão.
 */

export interface FormConferenciaProps {
  itens: ItemDoRomaneioParaConferencia[];
  /** A conferência já registrada, se houver — o bloco vira resumo + "Corrigir". */
  conferencia: ConferenciaDTO | null;
  onConferir: (dados: {
    bate: boolean;
    itens?: Array<{ variedade: string; qualidade: string | null; kg: number; precoKg?: number }>;
    observacao?: string;
    vencimento?: string | null;
  }) => void;
  salvando: boolean;
  erro?: string | null;
  /** SÓ no app do comprador: o vencimento entra no formulário. */
  comVencimento?: { atual: string | null; dataVenda: string };
  /**
   * Abre direto no formulário item a item — quem tocou em "Informar divergência"
   * já respondeu "Bate?", e perguntar de novo dentro do diálogo era um toque a
   * mais (print do Hugo, 24/09).
   */
  iniciarEditando?: boolean;
  /** Com `iniciarEditando`: "Cancelar" fecha quem abriu, em vez de voltar ao "Bate?". */
  onCancelar?: () => void;
}

function chave(i: { variedade: string; qualidade: string | null }): string {
  return `${i.variedade}|${i.qualidade ?? ""}`;
}

function rotulo(i: { variedade: string; qualidade: string | null }): string {
  return `${i.variedade}${i.qualidade ? ` ${i.qualidade}` : ""}`;
}

/** Aceita vírgula decimal — o teclado do celular manda "1.234,5". */
function parseKg(v: string): number {
  const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
  return isNaN(n) || n < 0 ? 0 : n;
}

/**
 * R$/kg: "1,40" ou "1.40". Diferente do kg, o ponto aqui é DECIMAL — ninguém
 * paga R$ 1.400,00 o quilo, e "1.40" lido como milhar viraria 140.
 * `NaN` = inválido (a tela avisa e não deixa registrar).
 */
function parsePreco(v: string): number {
  const t = String(v).trim();
  const n = parseFloat(t.indexOf(",") >= 0 ? t.replace(/\./g, "").replace(",", ".") : t);
  return isNaN(n) || n <= 0 ? NaN : Math.round(n * 100) / 100;
}

function fmtPrecoCampo(n: number): string {
  return n.toFixed(2).replace(".", ",");
}

export function FormConferencia({
  itens, conferencia, onConferir, salvando, erro, comVencimento, iniciarEditando = false, onCancelar,
}: FormConferenciaProps) {
  // Conferência válida já registrada → resumo. A INVALIDADA não conta: a venda
  // foi corrigida depois dela, e o comprador precisa conferir de novo (é
  // exatamente o que a pendência dele pede).
  const registrada = conferencia && !conferencia.invalidadaEm ? conferencia : null;

  // Pré-preenche com o ROMANEIO (ou com o que ele já conferiu): conferir é
  // corrigir o que veio diferente, não digitar tudo de novo.
  function valoresIniciais() {
    const kgs: Record<string, string> = {};
    const precos: Record<string, string> = {};
    for (const i of itens) {
      const jaConferido = registrada?.linhas.filter(l => chave(l) === chave(i))[0];
      kgs[chave(i)] = String(jaConferido ? jaConferido.kgConferido : i.kg).replace(".", ",");
      precos[chave(i)] = fmtPrecoCampo(jaConferido?.precoKgConferido ?? i.precoKg);
    }
    return { kgs, precos };
  }

  const [modo, setModo] = useState<"fechado" | "editando">(iniciarEditando ? "editando" : "fechado");
  const [kg, setKg] = useState<Record<string, string>>(() => (iniciarEditando ? valoresIniciais().kgs : {}));
  const [preco, setPreco] = useState<Record<string, string>>(() => (iniciarEditando ? valoresIniciais().precos : {}));
  const [observacao, setObservacao] = useState(iniciarEditando ? registrada?.observacao ?? "" : "");
  const [vencimento, setVencimento] = useState(comVencimento?.atual ?? "");

  function abrirEdicao() {
    const { kgs, precos } = valoresIniciais();
    setKg(kgs);
    setPreco(precos);
    setObservacao(registrada?.observacao ?? "");
    setVencimento(comVencimento?.atual ?? "");
    setModo("editando");
  }

  const kgDe = (i: ItemDoRomaneioParaConferencia) => parseKg(kg[chave(i)] ?? String(i.kg));
  const precoDe = (i: ItemDoRomaneioParaConferencia) => parsePreco(preco[chave(i)] ?? fmtPrecoCampo(i.precoKg));
  const precoInvalido = itens.filter(i => isNaN(precoDe(i)))[0];
  const totalKg = itens.reduce((acc, i) => acc + kgDe(i), 0);
  const totalValor = itens.reduce((acc, i) => acc + kgDe(i) * (isNaN(precoDe(i)) ? 0 : precoDe(i)), 0);
  const valorRomaneio = itens.reduce((acc, i) => acc + i.kg * i.precoKg, 0);
  const vencimentoMudou = !!comVencimento && vencimento !== (comVencimento.atual ?? "");
  const vencimentoAntesDaVenda = !!comVencimento && !!vencimento && vencimento < comVencimento.dataVenda;

  if (registrada && modo === "fechado") {
    return (
      <section className="rounded-xl border bg-background p-4" data-testid="bloco-conferido">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {registrada.bate ? "Conferido" : "Conferido com diferença"}
            </p>
            <p className="text-sm font-semibold mt-0.5">
              {fmtDataBR(registrada.registradoEm.slice(0, 10))} · {fmtKgBR(registrada.totalKgConferido)}
            </p>
            <p className="text-sm font-bold">
              {fmtMoedaBR(registrada.valorConferido)}
              {registrada.deltaKg !== 0 && (
                <span className={`ml-1.5 text-xs font-semibold ${registrada.deltaKg < 0 ? "text-erro" : "text-sucesso"}`}>
                  ({registrada.deltaKg > 0 ? "+" : "−"}{fmtKgBR(Math.abs(registrada.deltaKg))})
                </span>
              )}
            </p>
            {registrada.precoDivergente && (
              <p className="text-xs font-semibold text-atencao mt-0.5" data-testid="texto-preco-conferido">
                Preço ajustado · {registrada.deltaValor < 0 ? "−" : "+"}{fmtMoedaBR(Math.abs(registrada.deltaValor))} no romaneio
              </p>
            )}
            {registrada.observacao && (
              <p className="text-xs text-muted-foreground mt-1">{registrada.observacao}</p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={abrirEdicao} data-testid="button-corrigir-conferencia">
            Corrigir
          </Button>
        </div>
      </section>
    );
  }

  if (modo === "fechado") {
    return (
      <section className="rounded-xl border bg-background p-4 space-y-3" data-testid="bloco-conferir">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold">Bate com o combinado?</p>
        </div>
        <p className="text-xs text-muted-foreground">
          Confira os kg na sua balança e o preço por kg. O que você registrar aqui é o que o produtor vai ver.
        </p>
        {erro && <p className="text-xs text-erro" data-testid="texto-erro-conferencia">{erro}</p>}
        <div className="grid grid-cols-2 gap-2">
          <Button
            className="h-12"
            disabled={salvando}
            onClick={() => onConferir({ bate: true })}
            data-testid="button-bate"
          >
            <Check className="h-4 w-4 mr-1.5" />Bate
          </Button>
          <Button
            variant="outline"
            className="h-12"
            disabled={salvando}
            onClick={abrirEdicao}
            data-testid="button-diferente"
          >
            Diferente
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border bg-background p-4 space-y-3" data-testid="form-conferencia">
      <p className="text-sm font-semibold">O que veio, de verdade?</p>
      <p className="text-xs text-muted-foreground">
        Ajuste só o que veio diferente do combinado — {comVencimento ? "kg, preço por kg ou vencimento" : "kg ou preço por kg"}.
        O que você registrar passa a ser a base do valor.
      </p>

      <ul className="space-y-2">
        {itens.map(i => {
          const k = chave(i);
          const kgLinha = kgDe(i);
          const precoLinha = precoDe(i);
          const precoMudou = !isNaN(precoLinha) && Math.round((precoLinha - i.precoKg) * 100) !== 0;
          const kgMudou = Math.round((kgLinha - i.kg) * 100) !== 0;
          return (
            <li key={k} className="rounded-lg border p-2.5" data-testid={`linha-conferencia-${k}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium truncate">{rotulo(i)}</span>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  romaneio: {fmtKgBR(i.kg)} × {fmtMoedaBR(i.precoKg)}
                </span>
              </div>
              {/* Dois campos lado a lado, com rótulo em cima: em 390px o "kg ×
                  R$/kg = subtotal" numa linha só não cabe com dois inputs. */}
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Kg</span>
                  <Input
                    className={`text-right font-mono font-semibold h-11 ${kgMudou ? "border-atencao" : ""}`}
                    inputMode="decimal"
                    value={kg[k] ?? ""}
                    onChange={e => setKg(prev => ({ ...prev, [k]: e.target.value }))}
                    data-testid={`input-kg-${k}`}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">R$/kg</span>
                  <Input
                    className={`text-right font-mono font-semibold h-11 ${isNaN(precoLinha) ? "border-erro" : precoMudou ? "border-atencao" : ""}`}
                    inputMode="decimal"
                    value={preco[k] ?? ""}
                    onChange={e => setPreco(prev => ({ ...prev, [k]: e.target.value }))}
                    aria-invalid={isNaN(precoLinha)}
                    data-testid={`input-preco-${k}`}
                  />
                </label>
              </div>
              <p className="mt-1 text-right text-xs text-muted-foreground">
                = <b className="text-foreground">{fmtMoedaBR(kgLinha * (isNaN(precoLinha) ? 0 : precoLinha))}</b>
              </p>
            </li>
          );
        })}
      </ul>

      {comVencimento && (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <Label htmlFor="conf-venc" className="text-xs uppercase text-muted-foreground">Vencimento</Label>
            <span className="text-[11px] text-muted-foreground">
              romaneio: {comVencimento.atual ? fmtDataBR(comVencimento.atual) : "a definir"}
            </span>
          </div>
          {/* `<input type=date>`: no celular o teclado nativo JÁ é o calendário —
              o mesmo campo de todas as datas do app (ver SeletorVencimento). */}
          <Input
            id="conf-venc"
            type="date"
            className={`h-11 ${vencimentoAntesDaVenda ? "border-erro" : vencimentoMudou ? "border-atencao" : ""}`}
            min={comVencimento.dataVenda}
            value={vencimento}
            onChange={e => setVencimento(e.target.value)}
            data-testid="input-vencimento-conferencia"
          />
          {vencimentoAntesDaVenda && (
            <p className="text-xs text-erro">O vencimento não pode ser antes da data da venda.</p>
          )}
          {vencimentoMudou && !vencimentoAntesDaVenda && (
            <p className="text-xs text-muted-foreground">
              O vencimento é um só para vocês dois: ao registrar, ele muda no romaneio do produtor também, e ele vê que foi você.
            </p>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="conf-obs" className="text-xs uppercase text-muted-foreground">Observação (opcional)</Label>
        <Input
          id="conf-obs"
          value={observacao}
          onChange={e => setObservacao(e.target.value)}
          placeholder="ex.: duas caixas vieram machucadas"
          data-testid="input-observacao-conferencia"
        />
      </div>

      <div className="flex items-start justify-between border-t pt-2 text-sm">
        <span className="text-muted-foreground">Total conferido</span>
        <span className="text-right">
          <b className="block">{fmtKgBR(totalKg)}</b>
          <b className="block text-lg">{fmtMoedaBR(totalValor)}</b>
          {Math.round((totalValor - valorRomaneio) * 100) !== 0 && (
            <span className="block text-[11px] text-muted-foreground" data-testid="texto-valor-romaneio">
              romaneio: {fmtMoedaBR(valorRomaneio)}
            </span>
          )}
        </span>
      </div>

      {precoInvalido && (
        <p className="text-xs text-erro" data-testid="texto-preco-invalido">
          Preço por kg inválido em {rotulo(precoInvalido)} — use, por exemplo, 1,40.
        </p>
      )}
      {erro && <p className="text-xs text-erro" data-testid="texto-erro-conferencia">{erro}</p>}

      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          className="h-12"
          onClick={() => (onCancelar ? onCancelar() : setModo("fechado"))}
          disabled={salvando}
        >
          Cancelar
        </Button>
        <Button
          className="h-12"
          disabled={salvando || !!precoInvalido || vencimentoAntesDaVenda}
          onClick={() => onConferir({
            bate: false,
            itens: itens.map(i => ({ variedade: i.variedade, qualidade: i.qualidade, kg: kgDe(i), precoKg: precoDe(i) })),
            observacao: observacao.trim() || undefined,
            // Só quando MUDOU: conferir com o prazo que já estava não é "o
            // comprador mudou o vencimento". Vazio = "a definir".
            ...(vencimentoMudou ? { vencimento: vencimento || null } : {}),
          })}
          data-testid="button-registrar-conferencia"
        >
          {salvando ? "Registrando…" : "Registrar conferência"}
        </Button>
      </div>
    </section>
  );
}
